'use client'

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/tools/auth'
import { trackLogin } from '@/tools/device-detection/providers/incognia'
import { glovoRum, useErrorHandling } from '@/tools/o11y/client'
import { useToggles } from '@/tools/toggles/client'
import type { PhoneNumber, UserModel } from '@/user/entities'
import {
  fetchUserData,
  getUserPrimeSubscriptionStatus,
  isStaffUser as isStaff,
} from '@/user/use-cases'

export interface UserData {
  userData: UserModel | null
  setUserData: (newUserData: UserModel) => void
  setPhoneNumber: (phoneNumber: PhoneNumber) => void
  isPrimeUser: boolean | undefined
  isStaffUser: boolean | undefined
}

export const UserDataContext = createContext<UserData>({
  userData: null,
  setPhoneNumber: () => undefined,
  setUserData: () => null,
  isPrimeUser: false,
  isStaffUser: false,
})

export const UserDataProvider = ({
  children,
  initialData,
  userIsPrime,
}: {
  children: React.ReactNode
  initialData: UserModel | null
  userIsPrime: boolean | undefined
}) => {
  const { execWithErrorTracking } = useErrorHandling()
  const [isPrimeUser, setIsPrimeUser] = useState<boolean | undefined>(userIsPrime)
  const [isStaffUser, setIsStaffUser] = useState<boolean | undefined>()
  const [userData, setUserData] = useState<UserModel | null>(initialData)
  const { refreshToggles } = useToggles()
  const { isLoggedIn } = useAuth()

  const setPhoneNumber = (phoneNumber: PhoneNumber) => {
    setUserData((prevUserData) => {
      if (!prevUserData) {
        return prevUserData
      }
      return {
        ...prevUserData,
        phoneNumber,
      }
    })
  }

  useEffect(() => {
    execWithErrorTracking(async () => {
      if (isLoggedIn) {
        const userData = await fetchUserData()
        setUserData(userData)
        if (userData?.id) {
          await trackLogin(userData.id, userData.phoneNumber)
          const isPrimeUser = await getUserPrimeSubscriptionStatus(userData.id)
          setIsPrimeUser(isPrimeUser)
        }
        if (isStaff(userData?.email)) {
          setIsStaffUser(true)
        }
      } else {
        setUserData(null)
      }
      void refreshToggles()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn])

  useEffect(() => {
    execWithErrorTracking(async () => {
      if (isLoggedIn && userData) {
        glovoRum.setUser({ id: userData.id })
        glovoRum.setContext('authenticated', true)
      } else {
        glovoRum.setContext('authenticated', false)
        glovoRum.clearUser()
      }
    })
  }, [execWithErrorTracking, userData, isLoggedIn])

  const context = useMemo(
    () => ({ userData, setUserData, setPhoneNumber, isPrimeUser, isStaffUser }),
    [isPrimeUser, isStaffUser, userData]
  )

  return <UserDataContext.Provider value={context}>{children}</UserDataContext.Provider>
}

export const useUserData = () => useContext(UserDataContext)
